import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Loader2, Trash2, ShieldCheck, Smartphone, Monitor, Clock, MapPin,
  RefreshCw, LogOut, Save, Settings2,
} from "lucide-react";
import api from "@/lib/api";

function formatWhen(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}

function daysUntil(iso, expiredLabel) {
  if (!iso) return "—";
  const diff = (new Date(iso).getTime() - Date.now()) / 86400000;
  if (diff <= 0) return expiredLabel;
  return `${Math.ceil(diff)}d`;
}

export default function TrustedDevices() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState({});   // {id: bool} rows currently removing

  // Policy settings (Master Admin only) — values match /admin/security-config.
  const [policy, setPolicy] = useState({ max_mobile: 3, max_computer: 3, trust_days: 30 });
  const [policyDirty, setPolicyDirty] = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);

  const loadPolicy = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/security-config");
      setPolicy({
        max_mobile: Number(data.max_mobile) || 3,
        max_computer: Number(data.max_computer) || 3,
        trust_days: Number(data.trust_days) || 30,
      });
      setPolicyDirty(false);
    } catch {/* ignore — endpoint only visible to admins */}
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/trusted-devices");
      setItems(data.items || []);
      setConfig(data.config || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("trusted.failed_load"));
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(); loadPolicy(); }, [load, loadPolicy]);

  const updatePolicyField = (key, value) => {
    setPolicy((p) => ({ ...p, [key]: value }));
    setPolicyDirty(true);
  };

  const savePolicy = async () => {
    // Validation matches backend caps.
    const payload = {
      max_mobile: Math.max(1, Math.min(20, Number(policy.max_mobile) || 0)),
      max_computer: Math.max(1, Math.min(20, Number(policy.max_computer) || 0)),
      trust_days: Math.max(1, Math.min(365, Number(policy.trust_days) || 0)),
    };
    setSavingPolicy(true);
    try {
      const { data } = await api.put("/admin/security-config", payload);
      setPolicy({
        max_mobile: Number(data.max_mobile) || payload.max_mobile,
        max_computer: Number(data.max_computer) || payload.max_computer,
        trust_days: Number(data.trust_days) || payload.trust_days,
      });
      setPolicyDirty(false);
      toast.success(t("trusted.policy_saved", "Policy updated"));
      // Reload the top summary tiles to reflect the new caps.
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("trusted.policy_save_failed", "Could not save policy"));
    } finally { setSavingPolicy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("trusted.confirm_remove"))) return;
    setPending((p) => ({ ...p, [id]: true }));
    try {
      await api.delete(`/trusted-devices/${id}`);
      toast.success(t("trusted.device_removed"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("trusted.failed_remove"));
    } finally { setPending((p) => { const n = { ...p }; delete n[id]; return n; }); }
  };

  const forceLogout = async () => {
    if (!window.confirm(t("trusted.confirm_force_logout"))) return;
    try {
      const { data } = await api.post("/trusted-devices/force-logout");
      toast.success(t("trusted.revoked_n", { n: data.revoked }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("trusted.failed_force_logout"));
    }
  };

  const counts = useMemo(() => ({
    mobile: items.filter((x) => x.type === "mobile").length,
    computer: items.filter((x) => x.type === "computer").length,
  }), [items]);

  return (
    <div className="space-y-6" data-testid="trusted-devices-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-slate-500" /> {t("trusted.title")}
          </div>
          <h1
            className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {t("trusted.title")}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {t("trusted.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50"
            data-testid="trusted-reload"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> {t("trusted.reload")}
          </button>
          <button
            onClick={forceLogout}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-rose-50 text-rose-700 border border-rose-200 text-xs font-semibold hover:bg-rose-100"
            data-testid="trusted-force-logout"
          >
            <LogOut className="w-3.5 h-3.5" /> {t("trusted.sign_out_everywhere")}
          </button>
        </div>
      </div>

      {config && (
        <div className="grid grid-cols-3 gap-3">
          <SummaryTile
            icon={Smartphone}
            label={t("trusted.mobile_devices")}
            value={`${counts.mobile} / ${config.max_mobile}`}
            testid="trusted-count-mobile"
          />
          <SummaryTile
            icon={Monitor}
            label={t("trusted.computers")}
            value={`${counts.computer} / ${config.max_computer}`}
            testid="trusted-count-computer"
          />
          <SummaryTile
            icon={Clock}
            label={t("trusted.trust_period")}
            value="No expiration"
            testid="trusted-config-days"
          />
        </div>
      )}

      {/* Policy editor (Master Admin only — route already restricts access). */}
      <section className="bg-white rounded-md border border-slate-200 p-5" data-testid="trusted-policy-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-[#EDF5FF] text-[#0F62FE] flex items-center justify-center flex-shrink-0">
            <Settings2 className="w-5 h-5" strokeWidth={2} />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#0F62FE]">
              {t("trusted.policy_section", "Trusted device policy")}
            </div>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {t("trusted.policy_title", "Set the platform-wide limits")}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5 max-w-lg">
              {t("trusted.policy_hint", "Applies to every user account. Reducing a limit does not revoke existing trusted devices — it only blocks new ones once the cap is reached.")}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <PolicyField
            icon={Smartphone}
            label={t("trusted.max_mobile_label", "Maximum trusted mobile devices")}
            hint={t("trusted.max_mobile_hint", "Per user (1-20)")}
            value={policy.max_mobile}
            onChange={(v) => updatePolicyField("max_mobile", v)}
            min={1}
            max={20}
            testid="policy-max-mobile"
          />
          <PolicyField
            icon={Monitor}
            label={t("trusted.max_computer_label", "Maximum trusted computer devices")}
            hint={t("trusted.max_computer_hint", "Per user (1-20)")}
            value={policy.max_computer}
            onChange={(v) => updatePolicyField("max_computer", v)}
            min={1}
            max={20}
            testid="policy-max-computer"
          />
          <div className="flex items-start gap-3 p-3 rounded-lg border border-emerald-200 bg-emerald-50" data-testid="policy-trust-days">
            <div className="w-8 h-8 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
              <Clock className="w-4 h-4" strokeWidth={2} />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-800">Trust expiration</div>
              <div className="text-lg font-bold text-emerald-900 mt-0.5">No expiration</div>
              <div className="text-[11px] text-emerald-800/80 mt-0.5">Devices stay trusted until you revoke them.</div>
            </div>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button
            data-testid="policy-save-btn"
            onClick={savePolicy}
            disabled={!policyDirty || savingPolicy}
            className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {savingPolicy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {t("common.save", "Save")}
          </button>
        </div>
      </section>

      <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
        {loading && (
          <div className="px-5 py-10 text-center text-slate-500 text-sm">
            <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> {t("ui.loading_ellipsis")}
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="px-5 py-10 text-center text-slate-500 text-sm">
            {t("trusted.no_trusted_hint")}
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="divide-y divide-slate-100">
            {items.map((d) => {
              const TypeIcon = d.type === "mobile" ? Smartphone : Monitor;
              return (
                <div key={d.id} className="flex items-start gap-4 px-5 py-4" data-testid={`device-row-${d.id}`}>
                  <div className={`w-12 h-12 rounded-md flex items-center justify-center flex-shrink-0 ${d.type === "mobile" ? "bg-violet-50 text-violet-700" : "bg-blue-50 text-blue-700"}`}>
                    <TypeIcon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900 truncate">{d.name}</span>
                      <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                        {d.type}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1 font-mono truncate">
                      {d.browser} · {d.os} · {d.ip || t("trusted.no_ip")} {d.country ? `· ${d.country}` : ""}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      {t("trusted.added")} {formatWhen(d.added_at)} · {t("trusted.last_login")} {formatWhen(d.last_login_at)} · <span className="font-semibold text-emerald-700">Trusted permanently</span>
                    </div>
                  </div>
                  <button
                    onClick={() => remove(d.id)}
                    disabled={!!pending[d.id]}
                    data-testid={`device-remove-${d.id}`}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-slate-300 text-rose-600 text-xs font-semibold hover:bg-rose-50 disabled:opacity-40"
                  >
                    {pending[d.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    {t("trusted.remove")}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white rounded-md border border-slate-200 p-4 flex items-center gap-3" data-testid={testid}>
      <Icon className="w-8 h-8 text-[#0F62FE]" strokeWidth={1.6} />
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className="text-lg font-bold text-slate-900">{value}</div>
      </div>
    </div>
  );
}

function PolicyField({ icon: Icon, label, hint, value, onChange, min, max, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className="w-4 h-4 text-slate-500" />
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">{label}</div>
      </div>
      <input
        data-testid={testid}
        type="number"
        min={min}
        max={max}
        step="1"
        value={value}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-lg font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
      />
      <div className="text-[11px] text-slate-500 mt-1">{hint}</div>
    </div>
  );
}
